import React from "react";
import { Box, Chip, LinearProgress, Typography } from "@mui/material";
import { formatLocalDateTime } from "utils/formatLocalDateTime";
import { getBudgetTextColor } from "./budgetColors";

const ACCENT = "#26890d";
const USAGE_RED = "#E2231A";
const USAGE_ORANGE = "#F28C00";
const USAGE_YELLOW = "#FFCD00";
const getBudgetColor = (pct) => (pct >= 80 ? USAGE_RED : pct >= 50 ? USAGE_ORANGE : USAGE_YELLOW);

// Which budget actually applies to the user — same wording/colors on every page
// that shows it (Copilot users, AIC Budget Requests).
export const SCOPE_LABELS = {
  user: "ULB",
  multi_user_cost_center: "Cost Center Level Budget",
  multi_user_customer: "Universal Budget",
};
const SCOPE_STYLES = {
  user: { color: ACCENT, bg: "rgba(38,137,13,0.1)" },
  multi_user_cost_center: { color: "#0b5fa5", bg: "rgba(11,95,165,0.1)" },
  multi_user_customer: { color: "#6a4fb3", bg: "rgba(106,79,179,0.1)" },
};

const formatUsd = (v) => `$${Number(v).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// "X / Y used" with a progress bar, the scope that applies and when the budget
// record was last updated. The whole budget, and the amounts inside it, can each
// be null — never assume they're there.
//
// `scopeChip` lets a page keep its own chip (AIC Budget Requests labels it
// "… Applied"); without it a default chip from SCOPE_LABELS is used.
const BudgetUsageCell = ({ budget, scopeChip: customScopeChip }) => {
  if (!budget) {
    return <Typography sx={{ fontSize: "0.75rem", color: "#bbb", fontStyle: "italic" }}>No budget set</Typography>;
  }
  const { budget_amount: amount, consumed_amount: consumed, budget_scope: scope } = budget;
  const scopeStyle = SCOPE_STYLES[scope] ?? { color: "#666", bg: "rgba(0,0,0,0.06)" };
  const scopeChip =
    customScopeChip ?? (
      <Chip
        label={SCOPE_LABELS[scope] ?? (scope ? "Shared Budget" : "Budget")}
        size="small"
        sx={{ color: scopeStyle.color, backgroundColor: scopeStyle.bg, fontWeight: 700, fontSize: "0.62rem", height: "18px" }}
      />
    );
  if (amount == null) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography sx={{ fontSize: "0.8rem", color: "#888" }}>—</Typography>
        {scopeChip}
      </Box>
    );
  }
  const used = consumed ?? 0;
  const pct = amount > 0 ? Math.min((used / amount) * 100, 100) : 0;
  const barColor = getBudgetColor(pct);
  return (
    <Box sx={{ minWidth: 270 }}>
      {/* One line: amount, which budget applies, percentage — keeps the row short. */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.4 }}>
        <Typography sx={{ fontSize: "0.78rem", color: "#3a3a3a", whiteSpace: "nowrap" }}>
          {formatUsd(used)} / {formatUsd(amount)}
        </Typography>
        {scopeChip}
        <Typography sx={{ ml: "auto", fontSize: "0.72rem", fontWeight: 700, color: getBudgetTextColor(barColor) }}>
          {pct.toFixed(0)}%
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{ height: 5, borderRadius: 4, backgroundColor: "#f0f0f0", "& .MuiLinearProgress-bar": { backgroundColor: barColor, borderRadius: 4 } }}
      />
      {budget.updated_at && (
        <Typography sx={{ fontSize: "0.68rem", color: "#999", mt: 0.4 }}>
          Updated {formatLocalDateTime(budget.updated_at)}
        </Typography>
      )}
    </Box>
  );
};

export default BudgetUsageCell;
